/**
 * Overcut — articles magazine : ce qu'overcut-motion.js ne fait pas.
 *
 *   [data-oc-chapitres]  pastille flottante « 02/05 · Design » qui suit la
 *                        lecture et s'ouvre en sommaire. Construite à partir
 *                        des .oc-section du corps, qui reçoivent un id.
 *   [data-oc-incline]    carte qui pivote en 3D selon sa position à l'écran.
 *   [data-oc-partage]    partage natif sur mobile, copie du lien ailleurs.
 *   [data-oc-sortie]     ouverture dont le texte s'efface quand on commence à lire (--oc-sortie, 0 → 1).
 *   [data-oc-carrousel]  galerie à faire glisser : met à jour le compteur et la barre voisins.
 *
 * Sans JavaScript, rien de tout ça n'apparaît et l'article reste entier.
 */
(() => {
  if (window.OvercutArticle) return;
  const reduit = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Chapitres ---------- */

  function chapitres(racine) {
    const nav = racine.querySelector('[data-oc-chapitres]');
    const corps = racine.querySelector('[data-oc-chapitres-corps]');
    if (!nav || !corps) return;

    const sections = [...corps.querySelectorAll('.oc-section')];
    if (sections.length < 2) return;

    const liste = nav.querySelector('[data-oc-chapitres-liste]');
    const bouton = nav.querySelector('[data-oc-chapitres-bouton]');
    const numero = nav.querySelector('[data-oc-chapitres-numero]');
    const nom = nav.querySelector('[data-oc-chapitres-nom]');
    const total = String(sections.length).padStart(2, '0');

    const infos = sections.map((section, i) => {
      const tag = section.querySelector('.oc-section-tag');
      const titre = section.querySelector('.oc-section-title, h2');
      // « 02 · Design » : on garde le mot après le point médian.
      const brut = (tag ? tag.textContent : '').trim();
      const court = brut.includes('·') ? brut.split('·').pop().trim() : titre ? titre.textContent.trim() : '';
      const num = String(i + 1).padStart(2, '0');
      section.id = section.id || `chapitre-${i + 1}`;
      section.dataset.num = num;

      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${section.id}`;
      a.innerHTML = `<span>${num}</span>`;
      a.append(titre ? titre.textContent.trim() : court);
      li.append(a);
      liste.append(li);
      return { section, court, num, lien: a };
    });

    const ouvrir = (oui) => {
      nav.classList.toggle('is-ouvert', oui);
      bouton.setAttribute('aria-expanded', String(oui));
    };
    bouton.addEventListener('click', () => ouvrir(!nav.classList.contains('is-ouvert')));
    liste.addEventListener('click', (e) => {
      const a = e.target.closest('a');
      if (!a) return;
      e.preventDefault();
      const cible = document.querySelector(a.getAttribute('href'));
      ouvrir(false);
      cible?.scrollIntoView({ behavior: reduit.matches ? 'auto' : 'smooth', block: 'start' });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && nav.classList.contains('is-ouvert')) {
        ouvrir(false);
        bouton.focus();
      }
    });
    document.addEventListener('click', (e) => {
      if (!nav.contains(e.target)) ouvrir(false);
    });

    let actif = -1;
    let attente = false;
    const maj = () => {
      attente = false;
      const h = window.innerHeight;
      const r = corps.getBoundingClientRect();
      // Visible entre le haut du corps et sa fin : ni sur l'ouverture, ni sur les produits.
      nav.classList.toggle('is-visible', r.top < h * 0.5 && r.bottom > h * 0.6);

      let i = 0;
      infos.forEach((info, j) => {
        if (info.section.getBoundingClientRect().top < h * 0.4) i = j;
      });
      if (i === actif) return;
      actif = i;
      numero.textContent = `${infos[i].num}/${total}`;
      nom.textContent = infos[i].court;
      infos.forEach((info, j) => {
        info.lien.classList.toggle('is-lu', j < i);
        if (j === i) info.lien.setAttribute('aria-current', 'true');
        else info.lien.removeAttribute('aria-current');
      });
    };
    const surDefilement = () => {
      if (attente) return;
      attente = true;
      requestAnimationFrame(maj);
    };
    window.addEventListener('scroll', surDefilement, { passive: true });
    window.addEventListener('resize', surDefilement, { passive: true });
    nav.hidden = false;
    maj();
  }

  /* ---------- Cartes qui pivotent ---------- */

  function inclinaisons(racine) {
    if (reduit.matches) return;
    const cartes = [...racine.querySelectorAll('[data-oc-incline]')];
    if (!cartes.length) return;
    let attente = false;
    const maj = () => {
      attente = false;
      const h = window.innerHeight;
      for (const carte of cartes) {
        const r = carte.getBoundingClientRect();
        if (r.bottom < 0 || r.top > h) continue;
        // -1 quand la carte entre par le bas, 0 au centre, 1 quand elle sort par le haut.
        const p = Math.max(-1, Math.min(1, (r.top + r.height / 2 - h / 2) / (h / 2)));
        carte.style.setProperty('--oc-rx', `${(p * 14).toFixed(2)}deg`);
        carte.style.setProperty('--oc-ry', `${(p * -18).toFixed(2)}deg`);
        carte.style.setProperty('--oc-brillance', `${(50 - p * 40).toFixed(1)}%`);
      }
    };
    const surDefilement = () => {
      if (attente) return;
      attente = true;
      requestAnimationFrame(maj);
    };
    window.addEventListener('scroll', surDefilement, { passive: true });
    maj();
  }

  /* ---------- Partage ---------- */

  function partage(racine) {
    racine.querySelectorAll('[data-oc-partage]').forEach((bouton) => {
      const etiquette = bouton.querySelector('[data-oc-partage-texte]');
      const initial = etiquette ? etiquette.textContent : '';
      bouton.addEventListener('click', async () => {
        const donnees = { title: document.title, url: bouton.dataset.url || location.href };
        if (navigator.share && matchMedia('(pointer: coarse)').matches) {
          try {
            await navigator.share(donnees);
          } catch (e) {
            /* partage annulé : rien à faire */
          }
          return;
        }
        try {
          await navigator.clipboard.writeText(donnees.url);
          if (etiquette) {
            etiquette.textContent = bouton.dataset.copie || 'Lien copié';
            setTimeout(() => (etiquette.textContent = initial), 2200);
          }
        } catch (e) {
          window.prompt('', donnees.url);
        }
      });
    });
  }

  /* ---------- Ouverture qui s'efface au défilement ---------- */

  function sorties(racine) {
    if (reduit.matches) return;
    const blocs = [...racine.querySelectorAll('[data-oc-sortie]')];
    if (!blocs.length) return;
    let attente = false;
    const maj = () => {
      attente = false;
      for (const bloc of blocs) {
        // 0 tant que l'ouverture est entière à l'écran, 1 quand elle a fini de sortir par le haut.
        const r = bloc.getBoundingClientRect();
        const p = Math.max(0, Math.min(1, -r.top / Math.max(1, r.height * 0.7)));
        bloc.style.setProperty('--oc-sortie', p.toFixed(3));
      }
    };
    window.addEventListener(
      'scroll',
      () => {
        if (attente) return;
        attente = true;
        requestAnimationFrame(maj);
      },
      { passive: true }
    );
    maj();
  }

  /* ---------- Galerie à faire glisser : compteur et barre ---------- */

  function carrousels(racine) {
    racine.querySelectorAll('[data-oc-carrousel]').forEach((piste) => {
      if (piste.dataset.ocCarrouselPret) return;
      piste.dataset.ocCarrouselPret = '1';
      const galerie = piste.parentElement;
      const compte = galerie.querySelector('[data-oc-carrousel-compte]');
      const barre = galerie.querySelector('[data-oc-carrousel-barre]');
      const vues = [...piste.children];
      if (vues.length < 2) return;
      const total = String(vues.length).padStart(2, '0');
      let attente = false;
      const maj = () => {
        attente = false;
        // La vue courante est celle dont le bord gauche est le plus proche du début de la piste.
        const debut = piste.getBoundingClientRect().left;
        let i = 0;
        let ecart = Infinity;
        vues.forEach((vue, k) => {
          const d = Math.abs(vue.getBoundingClientRect().left - debut);
          if (d < ecart) {
            ecart = d;
            i = k;
          }
        });
        if (compte) compte.textContent = `${String(i + 1).padStart(2, '0')} / ${total}`;
        if (barre) barre.style.setProperty('--oc-avance', ((i + 1) / vues.length).toFixed(3));
      };
      piste.addEventListener(
        'scroll',
        () => {
          if (attente) return;
          attente = true;
          requestAnimationFrame(maj);
        },
        { passive: true }
      );
      // Clavier : la piste a le focus, les flèches passent d'une photo à l'autre.
      piste.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const pas = vues[0].getBoundingClientRect().width;
        piste.scrollBy({ left: e.key === 'ArrowRight' ? pas : -pas, behavior: reduit.matches ? 'auto' : 'smooth' });
      });
    });
  }

  function init(racine = document) {
    chapitres(racine);
    inclinaisons(racine);
    partage(racine);
    sorties(racine);
    carrousels(racine);
  }

  window.OvercutArticle = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
